import React from 'react'
import styled from 'styled-components'
import { Container, MOBILE } from '../CustomLanding.styled'
import { colors, skills } from '../content'

const SkillsGrid: React.FC = () => (
	<Section>
		<Container>
			<Header>
				<Title>{skills.title}</Title>
				<P>{skills.p1}</P>
				<P>{skills.p2}</P>
				<SwipeHint>
					<span>SWIPE</span>
					<Arrow>←</Arrow>
				</SwipeHint>
			</Header>
		</Container>
		<Track>
			{skills.cards.map(card => (
				<Card key={card.title} dark={card.dark}>
					<CardTitle dark={card.dark}>{card.title}</CardTitle>
					<CardText dark={card.dark}>{card.text}</CardText>
				</Card>
			))}
		</Track>
	</Section>
)

export default SkillsGrid

const Section = styled.section`
	background: #ffffff;
	padding: 64px 0;
	border-top: 1px solid rgba(22, 58, 132, 0.1);
	border-bottom: 1px solid rgba(22, 58, 132, 0.1);
	${MOBILE} {
		padding: 40px 0;
	}
`

const Header = styled.div`
	max-width: 760px;
	margin-bottom: 34px;
	${MOBILE} {
		margin-bottom: 0;
	}
`

const Title = styled.h2`
	font-weight: 700;
	font-size: clamp(24px, 2.2vw, 34px);
	line-height: 1.35;
	color: ${colors.navy};
	margin: 0 0 26px;
	${MOBILE} {
		font-size: 27px;
		line-height: 1.4;
		margin-bottom: 18px;
	}
`

const P = styled.p`
	font-size: 17px;
	line-height: 2;
	color: ${colors.body};
	margin: 0 0 14px;
	&:last-of-type {
		margin-bottom: 0;
	}
	${MOBILE} {
		font-size: 16px;
		line-height: 1.95;
		margin-bottom: 10px;
	}
`

const SwipeHint = styled.div`
	display: none;
	${MOBILE} {
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 8px 0 20px;
		font-size: 12px;
		font-weight: 600;
		letter-spacing: 0.08em;
		color: ${colors.blue};
		direction: ltr;
		justify-content: flex-end;
		span:first-child {
			font-family: ui-monospace, Menlo, monospace;
		}
	}
`

const Arrow = styled.span`
	font-size: 14px;
`

/* Desktop: 3-up responsive grid inside the container.
   Mobile: the artboard's edge-to-edge swipe row with scroll snapping. */
const Track = styled.div`
	max-width: 1180px;
	margin: 0 auto;
	padding: 0 32px;
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 20px;
	${MOBILE} {
		display: flex;
		gap: 12px;
		overflow-x: auto;
		scroll-snap-type: x proximity;
		padding: 2px 18px 6px;
		-webkit-overflow-scrolling: touch;
	}
`

const Card = styled.div<{ dark: boolean }>`
	border: 1px solid ${colors.line};
	border-radius: 20px;
	padding: 32px;
	background: ${p => (p.dark ? colors.navy : colors.paper)};
	&:hover {
		${p => (p.dark ? `background: ${colors.navyDeep};` : `border-color: ${colors.blue}; background: #ffffff;`)}
	}
	${MOBILE} {
		flex: 0 0 78%;
		scroll-snap-align: center;
		border-radius: 18px;
		padding: 24px 22px;
	}
`

const CardTitle = styled.div<{ dark: boolean }>`
	font-weight: 700;
	font-size: 20px;
	color: ${p => (p.dark ? colors.amber : colors.navy)};
	direction: ltr;
	text-align: right;
	margin-bottom: 14px;
	${MOBILE} {
		font-size: 21px;
		margin-bottom: 10px;
	}
`

const CardText = styled.p<{ dark: boolean }>`
	margin: 0;
	font-size: 17px;
	line-height: 1.95;
	color: ${p => (p.dark ? 'rgba(245,245,245,.82)' : colors.muted)};
	${MOBILE} {
		font-size: 15px;
		line-height: 1.9;
	}
`
